import React, { useEffect, useCallback, useState } from 'react'

import { useSettingsStore } from '../../stores/settings'
import { runtime } from '@deltachat-desktop/runtime-interface'
import { donationUrl } from '@deltachat-desktop/shared/constants'
import SettingsIconButton from './SettingsIconButton'
import ConnectivityButton from './ConnectivityButton'
import ChatsAndMedia from './ChatsAndMedia'
import Notifications from './Notifications'
import About from '../dialogs/About'
import Appearance from './Appearance'
import Advanced from './Advanced'
import { ExperimentalFeatures } from './ExperimentalFeatures'
import Profile from './Profile'
import Dialog, { DialogBody, DialogHeader } from '../Dialog'
import EditProfileDialog from '../dialogs/EditProfileDialog'
import SettingsSeparator, { SettingsEndSeparator } from './SettingsSeparator'
import useDialog from '../../hooks/dialog/useDialog'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import type { TranslationKey } from '@deltachat-desktop/shared/translationKeyType'

import styles from './styles.module.scss'

import type { DialogProps } from '../../contexts/DialogContext'

type SettingsView =
  | 'chats_and_media'
  | 'notifications'
  | 'appearance'
  | 'advanced'
  | 'experimental'

const SECTION_TITLE_KEYS: Record<SettingsView, TranslationKey> = {
  chats_and_media: 'pref_chats',
  notifications: 'pref_notifications',
  appearance: 'pref_appearance',
  advanced: 'menu_advanced',
  experimental: 'pref_experimental_features',
}

/**
 * Two-pane mode needs room for the 720px dialog plus some backdrop;
 * below this the settings stay the classic stacked-dialog flow.
 */
const WIDE_VIEWPORT_QUERY = '(min-width: 800px)'

function useIsWideViewport(): boolean {
  const [isWide, setIsWide] = useState(
    () => window.matchMedia(WIDE_VIEWPORT_QUERY).matches
  )
  useEffect(() => {
    const mediaQuery = window.matchMedia(WIDE_VIEWPORT_QUERY)
    const onChange = () => setIsWide(mediaQuery.matches)
    mediaQuery.addEventListener('change', onChange)
    return () => mediaQuery.removeEventListener('change', onChange)
  }, [])
  return isWide
}

/**
 * The settings navigation list: profile, the section entries, and the
 * standalone-dialog entries (connectivity, donate, help, about). Rendered
 * as the body of the classic narrow settings dialog and as the sidebar of
 * the wide two-pane layout — only what a section click does differs.
 */
function SettingsNav({
  onSelectSection,
  activeSection,
}: {
  onSelectSection: (section: SettingsView) => void
  /** set in the two-pane layout to highlight the visible section */
  activeSection?: SettingsView
}) {
  const { openDialog } = useDialog()
  const settingsStore = useSettingsStore()[0]
  const tx = useTranslationFunction()

  return (
    <>
      {settingsStore != null && (
        <Profile
          settingsStore={settingsStore}
          onStatusClick={() => {
            openDialog(EditProfileDialog, {
              settingsStore,
            })
          }}
        />
      )}
      <SettingsSeparator />
      <SettingsIconButton
        icon='forum'
        active={activeSection === 'chats_and_media'}
        onClick={() => onSelectSection('chats_and_media')}
      >
        {tx('pref_chats')}
      </SettingsIconButton>
      <SettingsIconButton
        icon='bell'
        active={activeSection === 'notifications'}
        onClick={() => onSelectSection('notifications')}
      >
        {tx('pref_notifications')}
      </SettingsIconButton>
      <SettingsIconButton
        icon='brightness-6'
        active={activeSection === 'appearance'}
        onClick={() => onSelectSection('appearance')}
      >
        {tx('pref_appearance')}
      </SettingsIconButton>
      <ConnectivityButton />
      <SettingsIconButton
        icon='code-tags'
        active={activeSection === 'advanced'}
        onClick={() => onSelectSection('advanced')}
        dataTestid='open-advanced-settings'
      >
        {tx('menu_advanced')}
      </SettingsIconButton>
      <SettingsIconButton
        icon='test-tube'
        active={activeSection === 'experimental'}
        onClick={() => onSelectSection('experimental')}
      >
        {tx('pref_experimental_features')}
      </SettingsIconButton>
      <SettingsSeparator />
      {!runtime.getRuntimeInfo().isMac && (
        <SettingsIconButton
          icon='favorite'
          onClick={() => runtime.openLink(donationUrl)}
          isLink
        >
          {tx('donate')}
        </SettingsIconButton>
      )}
      <SettingsIconButton
        icon='question_mark'
        onClick={() => runtime.openHelpWindow()}
      >
        {tx('menu_help')}
      </SettingsIconButton>
      <SettingsIconButton icon='info' onClick={() => openDialog(About)}>
        {tx('global_menu_help_about_desktop')}
      </SettingsIconButton>
      <SettingsEndSeparator />
    </>
  )
}

/** The content of one settings section, shared by both layouts. */
function SettingsSectionBody({
  section,
  closeSettings,
}: {
  section: SettingsView
  /** closes the whole settings UI (all dialogs), e.g. after "View Log" */
  closeSettings: () => void
}) {
  const settingsStore = useSettingsStore()[0]

  return (
    <>
      {section === 'chats_and_media' && settingsStore != null && (
        <ChatsAndMedia settingsStore={settingsStore} />
      )}
      {section === 'notifications' && settingsStore != null && (
        <Notifications desktopSettings={settingsStore.desktopSettings} />
      )}
      {section === 'appearance' && settingsStore != null && (
        <Appearance
          rc={settingsStore.rc}
          desktopSettings={settingsStore.desktopSettings}
          settingsStore={settingsStore}
        />
      )}
      {section === 'advanced' && <Advanced onClose={closeSettings} />}
      {section === 'experimental' && <ExperimentalFeatures />}
      <SettingsEndSeparator />
    </>
  )
}

export default function Settings({ onClose }: DialogProps) {
  const { openDialog } = useDialog()
  const tx = useTranslationFunction()
  const isWideViewport = useIsWideViewport()
  const [activeSection, setActiveSection] =
    useState<SettingsView>('chats_and_media')

  useEffect(() => {
    if (window.__settingsOpened) {
      throw new Error(
        'Settings window was already open - this should not happen, please file a bug'
      )
    }
    window.__settingsOpened = true
    return () => {
      window.__settingsOpened = false
    }
  }, [])

  const openSettingsSectionDialog = useCallback(
    (settingsMode: SettingsView) => {
      openDialog(SettingsSectionDialog, {
        settingsMode,
        closeParentDialog: onClose,
      })
    },
    [onClose, openDialog]
  )

  if (isWideViewport) {
    // Discord/macOS-style two-pane layout: navigation sidebar + content pane
    // in one dialog, no stacked section dialogs.
    return (
      <Dialog
        onClose={onClose}
        fixed
        width={720}
        height='min(700px, 90vh)'
        dataTestid='settings-dialog'
        noTopPadding
      >
        <div className={styles.settingsTwoPane}>
          <nav className={styles.settingsSidebar}>
            <SettingsNav
              onSelectSection={setActiveSection}
              activeSection={activeSection}
            />
          </nav>
          <div className={styles.settingsPane}>
            <DialogHeader
              title={tx(SECTION_TITLE_KEYS[activeSection])}
              onClose={onClose}
              dataTestid={
                activeSection === 'advanced' ? 'settings-advanced' : 'settings'
              }
            />
            <DialogBody>
              <SettingsSectionBody
                section={activeSection}
                closeSettings={onClose}
              />
            </DialogBody>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      onClose={onClose}
      fixed
      width={400}
      dataTestid='settings-dialog'
      fullscreenOnMobile
    >
      <DialogHeader
        title={tx('menu_settings')}
        onClose={onClose}
        dataTestid='settings'
      />
      <DialogBody>
        <SettingsNav onSelectSection={openSettingsSectionDialog} />
      </DialogBody>
    </Dialog>
  )
}

function SettingsSectionDialog(
  props: {
    settingsMode: SettingsView
    closeParentDialog: () => void
  } & DialogProps
) {
  const { settingsMode, onClose: closeThisDialog } = props
  const tx = useTranslationFunction()

  const closeThisAndParent = useCallback(() => {
    closeThisDialog()
    props.closeParentDialog()
  }, [closeThisDialog, props])

  return (
    <Dialog onClose={closeThisDialog} fixed width={400} fullscreenOnMobile>
      <DialogHeader
        title={tx(SECTION_TITLE_KEYS[settingsMode])}
        onClickBack={closeThisDialog}
        onClose={closeThisAndParent}
        dataTestid={
          settingsMode === 'advanced' ? 'settings-advanced' : undefined
        }
      />
      <DialogBody>
        <SettingsSectionBody
          section={settingsMode}
          closeSettings={closeThisAndParent}
        />
      </DialogBody>
    </Dialog>
  )
}
